import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import {
  Close,
  Login,
  PersonAdd,
  Favorite,
} from '@mui/icons-material';
import AuthModal from './AuthModal';

interface SignInPromptModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
}

const SignInPromptModal: React.FC<SignInPromptModalProps> = ({
  open,
  onClose,
  title = "Sign In Required",
  message = "You need to sign in to save products to your favorites."
}) => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<'login' | 'register'>('login');

  const handleSignIn = () => {
    setAuthModalTab('login');
    setAuthModalOpen(true);
  };

  const handleSignUp = () => {
    setAuthModalTab('register');
    setAuthModalOpen(true);
  };

  const handleAuthModalClose = () => {
    setAuthModalOpen(false);
    onClose(); // Also close the prompt modal when auth modal closes
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Favorite sx={{ color: '#ff6b6b', fontSize: 28 }} />
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.04)',
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sign in to your account or create a new one to start saving your favorite products!
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        gap: 2,
        flexDirection: { xs: 'column', sm: 'row' },
        '& > *': {
          flex: { xs: 1, sm: 'none' },
          minWidth: { xs: '100%', sm: 'auto' }
        }
      }}>
        <Button
          variant="outlined"
          startIcon={<PersonAdd />}
          onClick={handleSignUp}
          sx={{
            borderColor: '#667eea',
            color: '#667eea',
            textTransform: 'none',
            fontWeight: 'bold',
            py: 1.5,
            px: 3,
            '&:hover': {
              borderColor: '#5a6fd8',
              bgcolor: 'rgba(102, 126, 234, 0.04)',
            }
          }}
        >
          Create Account
        </Button>
        <Button
          variant="contained"
          startIcon={<Login />}
          onClick={handleSignIn}
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            textTransform: 'none',
            fontWeight: 'bold',
            py: 1.5,
            px: 3,
            boxShadow: 'none',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
            }
          }}
        >
          Sign In
        </Button>
      </DialogActions>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={handleAuthModalClose}
        initialTab={authModalTab}
      />
    </Dialog>
  );
};

export default SignInPromptModal;
